/**
 * F-004 — Physics world wrapper.
 *
 * Thin façade over cannon-es so the rest of the codebase imports its physics
 * surface from here. Default gravity is set to (0, 0, 0) — Cosmic Suika's
 * "down" is replaced by the central GravityWell (F-005), so a constant
 * world-vector gravity would fight the well rather than complement it.
 */

import { ContactMaterial, Material, World, type Body } from "cannon-es";

export interface PhysicsWorld {
  world: World;
  /** Step the simulation forward by ``dt`` seconds. */
  step(dt: number): void;
  add(body: Body): void;
  remove(body: Body): void;
  bodies(): ReadonlyArray<Body>;
}

const FIXED_TIME_STEP = 1 / 60;

export function createPhysicsWorld(): PhysicsWorld {
  const world = new World();
  // Zero global gravity — see file docstring.
  world.gravity.set(0, 0, 0);
  // Modest broadphase default; F-035 may revisit when planet count climbs.
  world.broadphase.dirty = true;
  world.allowSleep = true;

  // Default contact material — meteors stick to surface (low restitution
  // = no bouncy, moderate friction so they grip rather than slide).
  const defaultMat = new Material("default");
  world.defaultMaterial = defaultMat;
  const contact = new ContactMaterial(defaultMat, defaultMat, {
    friction: 0.6,
    restitution: 0.05,
  });
  world.addContactMaterial(contact);
  world.defaultContactMaterial = contact;

  const step = (dt: number): void => {
    world.step(FIXED_TIME_STEP, dt, 3);
  };

  const add = (body: Body): void => {
    world.addBody(body);
  };

  const remove = (body: Body): void => {
    world.removeBody(body);
  };

  const bodies = (): ReadonlyArray<Body> => world.bodies;

  return { world, step, add, remove, bodies };
}

export const __testing = { FIXED_TIME_STEP };
